import { useEffect, useRef, useState } from "react";
import { Play, Pause, Maximize2, Volume2, VolumeX } from "lucide-react";
import { img } from "@/lib/api";

const igEmbed = (url) => {
  const m = url.match(/instagram\.com\/(reel|p)\/([\w-]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed` : null;
};

export function ReelCard({ r }) {
  const ref = useRef(null);
  const box = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) v.play().catch(() => {}); else v.pause(); }, { threshold: 0.5 });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  if (r.source === "instagram") {
    const src = igEmbed(r.url);
    return (
      <div className="relative aspect-[9/16] bg-[#171717] overflow-hidden" data-testid={`reel-card-${r.id}`}>
        {src ? <iframe src={src} title={r.title} className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media; fullscreen" loading="lazy" /> : <p className="p-4 text-sm text-grey">Invalid Instagram URL</p>}
      </div>
    );
  }

  const toggle = () => { const v = ref.current; v.paused ? v.play() : v.pause(); };
  const full = () => { const v = ref.current; (v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen)?.call(v); v.muted = false; setMuted(false); };
  return (
    <div ref={box} className="group relative aspect-[9/16] bg-[#171717] overflow-hidden" data-testid={`reel-card-${r.id}`}>
      <video ref={ref} src={img(r.url)} poster={img(r.thumbnail)} muted={muted} loop playsInline preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onClick={toggle} className="absolute inset-0 w-full h-full object-cover cursor-pointer" />
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 to-transparent pointer-events-none">
        <p className="eyebrow text-[10px]">{r.category}</p>
        <p className="font-semibold text-sm mt-1 leading-tight">{r.title}</p>
      </div>
      <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={toggle} data-testid={`reel-play-toggle-${r.id}`} className="w-9 h-9 bg-black/60 backdrop-blur grid place-items-center hover:bg-neon hover:text-black" aria-label="Play/Pause">{playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
        <button onClick={() => setMuted((m) => !m)} data-testid={`reel-mute-toggle-${r.id}`} className="w-9 h-9 bg-black/60 backdrop-blur grid place-items-center hover:bg-neon hover:text-black" aria-label="Mute">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
        <button onClick={full} data-testid={`reel-fullscreen-${r.id}`} className="w-9 h-9 bg-black/60 backdrop-blur grid place-items-center hover:bg-neon hover:text-black" aria-label="Fullscreen"><Maximize2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
